import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Calendar,
  ChevronDown,
  ChevronRight,
  Copy,
  DollarSign,
  ExternalLink,
  Globe,
  Monitor,
  MousePointer,
  Percent,
  Route,
  Send,
  Smartphone,
  TrendingUp,
  Users,
} from "lucide-react";

import { API, useAuth } from "../App";
import { useT } from "../i18n";
import { setBreadcrumbName } from "../lib/breadcrumbNames";
import CampaignParamsAndPostbacks from "./CampaignParamsAndPostbacks";
import CampaignMaintenance from "./CampaignMaintenance";
import ConversionTimeline from "./ConversionTimeline";
import IncomingPostbacks from "./IncomingPostbacks";
import ClickIssues from "./ClickIssues";
import GeoMap from "./GeoMap";
import PublicDashboardLinks from "./PublicDashboardLinks";
import PoolLinkGenerator from "./PoolLinkGenerator";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";

const CLOAK_REASON_LABELS = {
  bot: "Bots",
  fake_crawler: "Spoofed crawlers",
  verified_crawler: "Verified crawlers",
  datacenter: "Datacenter / Tor",
  proxy: "Proxy / VPN",
  ad_review: "Google Ads review",
  google_network: "Google networks",
  cloak_error: "Classification error (safe page)",
};

export default function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, locale } = useT();
  // Tabs: overview, audience, clicks, settings. Kept in ?tab= so a reload or
  // the back button returns to the same tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const tab = ["overview", "audience", "clicks", "settings"].includes(requestedTab) ? requestedTab : "overview";
  const selectTab = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === "overview") params.delete("tab");
    else params.set("tab", next);
    setSearchParams(params, { replace: true });
  };

  const [campaign, setCampaign] = useState(null);
  const [stats, setStats] = useState(null);
  const [geoStats, setGeoStats] = useState(null);
  const [clicks, setClicks] = useState([]);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [linkedForwarders, setLinkedForwarders] = useState([]);
  const [cloakingStats, setCloakingStats] = useState(null);
  const [adapterSnippet, setAdapterSnippet] = useState('');
  const [snippets, setSnippets] = useState(null);
  // Integration snippets are rarely needed, so the card is collapsed by
  // default to keep the campaign page compact; click the header to expand.
  const [showIntegration, setShowIntegration] = useState(false);

  useEffect(() => {
    if (!id || !user) return;
    let active = true;
    axios
      .get(`${API}/campaigns/${id}/adapter-snippet`)
      .then((r) => { if (active) setAdapterSnippet((r.data && r.data.snippet) || ''); })
      .catch(() => {});
    axios
      .get(`${API}/campaigns/${id}/integration-code`)
      .then((r) => { if (active) setSnippets((r.data && r.data.snippets) || null); })
      .catch(() => {});
    return () => { active = false; };
  }, [id, user]);

  useEffect(() => {
    if (!id || !user) return;

    let active = true;

    const loadData = async () => {
      setLoading(true);
      setNotFound(false);

      const [
        campaignResult,
        statsResult,
        clicksResult,
        geoResult,
        domainsResult,
        forwardersResult,
        cloakingResult,
      ] = await Promise.allSettled([
        axios.get(`${API}/campaigns/${id}`),
        axios.get(`${API}/campaigns/${id}/stats`),
        axios.get(`${API}/campaigns/${id}/clicks`),
        axios.get(`${API}/campaigns/${id}/geo-stats`),
        axios.get(`${API}/domains`),
        axios.get(`${API}/conversion-forwarders`),
        axios.get(`${API}/campaigns/${id}/cloaking-stats`),
      ]);

      if (!active) return;

      if (campaignResult.status === "fulfilled") {
        const campaignData = campaignResult.value?.data || null;
        setCampaign(campaignData);
        // Show the campaign name (not the raw id) in the breadcrumb topbar.
        setBreadcrumbName(campaignData?.id, campaignData?.name);
      } else {
        console.error("Error fetching campaign:", campaignResult.reason);
        setCampaign(null);

        if (campaignResult.reason?.response?.status === 404) {
          setNotFound(true);
          toast.error(t("Campaign not found"));
        }
      }

      if (statsResult.status === "fulfilled") {
        const data = statsResult.value?.data || {};
        const rev = Number(data.revenue || 0);
        const cost = Number(data.cost || 0);
        setStats({
          total_clicks: Number(data.total_clicks || 0),
          unique_clicks: Number(data.unique_clicks || 0),
          conversions: Number(data.conversions || 0),
          revenue: rev,
          cost,
          roi: cost > 0 ? ((rev - cost) / cost) * 100 : 0,
          conversion_rate: Number(data.conversion_rate || 0),
          epc: Number(data.epc || 0),
          clicks_by_device: Array.isArray(data.clicks_by_device)
            ? data.clicks_by_device
            : [],
          clicks_by_browser: Array.isArray(data.clicks_by_browser)
            ? data.clicks_by_browser
            : [],
        });
      } else {
        console.error("Error fetching stats:", statsResult.reason);
        setStats(null);
      }

      if (clicksResult.status === "fulfilled") {
        const raw = clicksResult.value?.data;
        // Handle both paginated response {items:[...]} and legacy array
        const clicksData = Array.isArray(raw) ? raw : Array.isArray(raw?.items) ? raw.items : [];
        setClicks(clicksData.slice(0, 10));
      } else {
        console.error("Error fetching clicks:", clicksResult.reason);
        setClicks([]);
      }

      if (geoResult.status === "fulfilled") {
        setGeoStats(geoResult.value?.data || null);
      } else {
        console.error("Error fetching geo stats:", geoResult.reason);
        setGeoStats(null);
      }

      if (domainsResult.status === "fulfilled") {
        setDomains(Array.isArray(domainsResult.value?.data) ? domainsResult.value.data : []);
      }

      if (forwardersResult.status === "fulfilled") {
        const allFw = forwardersResult.value?.data || [];
        setLinkedForwarders(
          allFw.filter(fw => {
            const ids = fw.campaign_ids || [];
            return ids.length === 0 || ids.includes(id);
          })
        );
      }

      if (cloakingResult.status === "fulfilled") {
        setCloakingStats(cloakingResult.value?.data || null);
      } else {
        setCloakingStats(null);
      }

      setLoading(false);
    };

    loadData();

    return () => {
      active = false;
    };
  }, [id, user, t]);

  const TRACKER_ALIASES = ['r', 'go', 'click', 'track', 'c'];

  const buildTrackingUrl = (cam, alias = 'r') => {
    const safeAlias = TRACKER_ALIASES.includes(alias) ? alias : 'r';
    const domainId = cam?.domain_id;
    if (domainId) {
      const domain = domains.find((d) => d.id === domainId);
      if (domain?.hostname) {
        return `https://${domain.hostname}/${safeAlias}/${cam.tracking_id}`;
      }
    }
    const apiBase = String(API || '').replace(/\/api\/?$/, '');
    return `${apiBase}/${safeAlias}/${cam.tracking_id}`;
  };

  const copyTrackingUrl = async () => {
    if (!campaign?.tracking_id) return;

    const trackingUrl = buildTrackingUrl(campaign);

    try {
      await navigator.clipboard.writeText(trackingUrl);
      toast.success(t("Tracking link copied!"));
    } catch (error) {
      console.error("Error copying tracking URL:", error);
      toast.error(t("Failed to copy tracking link"));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return t("Unknown");

    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return t("Unknown");

    return date.toLocaleString(locale === "ru" ? "ru-RU" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getDeviceIcon = (deviceType) => {
    switch (deviceType) {
      case "Mobile":
        return <Smartphone className="w-4 h-4" />;
      case "Desktop":
        return <Monitor className="w-4 h-4" />;
      default:
        return <Globe className="w-4 h-4" />;
    }
  };

  const openTargetUrl = () => {
    if (!campaign?.target_url) return;
    window.open(campaign.target_url, "_blank", "noopener,noreferrer");
  };

  const renderBreakdown = (items, title, iconRenderer) => {
    if (!items.length) {
      return (
        <Card>
          <CardHeader>
            <CardTitle>{title}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="py-4 text-center text-muted-foreground">
              {t("No data to display")}
            </p>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {items.map((item, index) => {
              const total = Number(stats?.total_clicks || 0);
              const count = Number(item?.count || 0);
              const width = total > 0 ? (count / total) * 100 : 0;

              return (
                <div
                  key={`${item?._id || "unknown"}-${index}`}
                  className="flex items-center justify-between"
                >
                  <div className="flex items-center space-x-2">
                    {iconRenderer(item)}
                    <span className="text-sm font-medium">
                      {item?._id || t("Unknown")}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-muted-foreground">
                      {t("{n} clicks", { n: count })}
                    </span>
                    <div className="h-2 w-16 rounded-full bg-surface-2">
                      <div
                        className="h-2 rounded-full bg-primary"
                        style={{ width: `${width}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="skeleton mb-6 h-8 w-64" />
        <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-3">
          {[...Array(3)].map((_, index) => (
            <Card key={index} className="skeleton h-32" />
          ))}
        </div>
        <div className="skeleton h-96 w-full" />
      </div>
    );
  }

  if (notFound || !campaign) {
    return (
      <div className="p-8">
        <Card>
          <CardContent className="py-10 text-center">
            <p className="text-muted-foreground">{t("Campaign not found")}</p>
            <div className="mt-4">
              <Button variant="outline" onClick={() => navigate("/campaigns")}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t("Back")}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const trackingUrl = buildTrackingUrl(campaign);
  const backendRoot = String(API || '').replace(/\/api\/?$/, '');
  const clickBody = JSON.stringify({ tracking_id: campaign.tracking_id || '', ip: '1.2.3.4', user_agent: 'Mozilla/5.0' });
  const clickCurl = "curl -X POST '" + backendRoot + "/v1/click' -H 'Authorization: Bearer $XPNSR_API_KEY' -H 'Content-Type: application/json' -d '" + clickBody + "'";
  const hasAnalytics =
    (stats?.clicks_by_device || []).length > 0 ||
    (stats?.clicks_by_browser || []).length > 0;

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center space-x-4">
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("Back")}
          </Button>

          <div>
            <div className="mb-1 flex items-center space-x-2">
              <h1
                className="font-display text-3xl font-extrabold tracking-[-0.03em] text-foreground"
                data-testid="campaign-detail-title"
              >
                {campaign.name || t("Unknown")}
              </h1>
              <Badge variant={campaign.is_active ? "default" : "secondary"}>
                {campaign.is_active ? t("Active") : t("Paused")}
              </Badge>
            </div>
            <p className="text-muted-foreground">
              {campaign.description || t("No description")}
            </p>
          </div>
        </div>

        <div className="flex space-x-2">
          <Button variant="outline" asChild>
            <Link to={`/campaigns/${campaign.id}/streams`}>
              <Route className="mr-2 h-4 w-4" />
              {t("Streams")}
            </Link>
          </Button>

          <Button variant="outline" onClick={openTargetUrl}>
            <ExternalLink className="mr-2 h-4 w-4" />
            {t("Open target URL")}
          </Button>

          <Button onClick={copyTrackingUrl} data-testid="copy-tracking-url">
            <Copy className="mr-2 h-4 w-4" />
            {t("Copy tracking link")}
          </Button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 gap-6 md:grid-cols-3 lg:grid-cols-6">
          <Card className="stats-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {t("Total clicks")}
              </CardTitle>
              <MousePointer className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div
                className="text-2xl font-bold text-foreground"
                data-testid="total-clicks"
              >
                {stats.total_clicks}
              </div>
              <p className="text-xs text-muted-foreground">
                {t("Total clicks")}
              </p>
            </CardContent>
          </Card>

          <Card className="stats-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {t("Unique clicks")}
              </CardTitle>
              <Users className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div
                className="text-2xl font-bold text-foreground"
                data-testid="unique-clicks"
              >
                {stats.unique_clicks}
              </div>
              <p className="text-xs text-muted-foreground">
                {t("Unique visitors")}
              </p>
            </CardContent>
          </Card>

          <Card className="stats-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {t("Conversions")}
              </CardTitle>
              <TrendingUp className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div
                className="text-2xl font-bold text-foreground"
                data-testid="conversions"
              >
                {stats.conversions}
              </div>
              <p className="text-xs text-muted-foreground">
                {t("Successful conversions")}
              </p>
            </CardContent>
          </Card>

          <Card className="stats-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {t("Conversion rate")}
              </CardTitle>
              <TrendingUp className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div
                className="text-2xl font-bold text-foreground"
                data-testid="conversion-rate"
              >
                {Number(stats.conversion_rate || 0).toFixed(1)}%
              </div>
              <p className="text-xs text-muted-foreground">
                {t("Conversion percentage")}
              </p>
            </CardContent>
          </Card>

          <Card className="stats-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {t("Revenue")}
              </CardTitle>
              <TrendingUp className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-foreground" data-testid="revenue">
                ${Number(stats.revenue || 0).toFixed(2)}
              </div>
              <p className="text-xs text-muted-foreground">
                {t("Total revenue")}
              </p>
            </CardContent>
          </Card>

          <Card className="stats-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {t("EPC")}
              </CardTitle>
              <DollarSign className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-foreground" data-testid="epc">
                ${Number(stats.epc || 0).toFixed(3)}
              </div>
              <p className="text-xs text-muted-foreground">
                {t("Earnings per click")}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {stats && (stats.cost > 0 || stats.roi !== 0) && (
        <div className="grid grid-cols-2 gap-6 md:grid-cols-3">
          <Card className="stats-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">{t("Cost")}</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-foreground">${stats.cost.toFixed(2)}</div>
            </CardContent>
          </Card>
          <Card className="stats-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">{t("Profit")}</CardTitle>
              <TrendingUp className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${(stats.revenue - stats.cost) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                ${(stats.revenue - stats.cost).toFixed(2)}
              </div>
            </CardContent>
          </Card>
          <Card className="stats-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">{t("ROI")}</CardTitle>
              <Percent className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${stats.roi >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {stats.roi.toFixed(1)}%
              </div>
            </CardContent>
          </Card>
        </div>
      )}


      <Tabs value={tab} onValueChange={selectTab} className="space-y-6">
        <TabsList className="flex w-full flex-wrap h-auto justify-start" data-testid="campaign-tabs">
          <TabsTrigger value="overview">{t("Overview")}</TabsTrigger>
          <TabsTrigger value="audience">{t("Audience")}</TabsTrigger>
          <TabsTrigger value="clicks">{t("Clicks")}</TabsTrigger>
          <TabsTrigger value="settings">{t("Settings")}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
      {/* Conversions with status timeline */}
      <div className="space-y-2">
        <h3 className="text-lg font-semibold">{t("Conversions")}</h3>
        <ConversionTimeline campaignId={id} />
      </div>

      {cloakingStats && (cloakingStats.cloaking_enabled || cloakingStats.cloaked > 0) && (
        <Card data-testid="cloaking-stats-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {t("Cloaking")}
              <Badge variant={cloakingStats.cloaking_enabled ? "default" : "secondary"}>
                {cloakingStats.cloaking_enabled ? t("Enabled") : t("Disabled")}
              </Badge>
            </CardTitle>
            <CardDescription>
              {t("Bots and crawlers sent to the safe page vs humans to the offer")}
              {" · "}
              {t("last {n} days").replace("{n}", String(cloakingStats.window_days || 7))}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <div>
                <div className="text-2xl font-bold text-foreground" data-testid="cloak-cloaked">
                  {cloakingStats.cloaked}
                </div>
                <p className="text-xs text-muted-foreground">{t("Cloaked → safe page")}</p>
              </div>
              <div>
                <div className="text-2xl font-bold text-foreground" data-testid="cloak-money">
                  {cloakingStats.money}
                </div>
                <p className="text-xs text-muted-foreground">{t("Real → offer")}</p>
              </div>
              <div>
                <div className="text-2xl font-bold text-foreground" data-testid="cloak-rate">
                  {Math.round((cloakingStats.cloak_rate || 0) * 100)}%
                </div>
                <p className="text-xs text-muted-foreground">{t("Cloak rate")}</p>
              </div>
            </div>
            {cloakingStats.cloaked > 0 && (
              <div className="space-y-1">
                {Object.entries(cloakingStats.by_reason || {})
                  .filter(([, count]) => count > 0)
                  .map(([reason, count]) => (
                    <div key={reason} className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        {t(CLOAK_REASON_LABELS[reason] || reason)}
                      </span>
                      <Badge variant="outline">{count}</Badge>
                    </div>
                  ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

        </TabsContent>

        <TabsContent value="audience" className="space-y-6">
      <GeoMap geoData={geoStats} />
      {hasAnalytics && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {renderBreakdown(
            stats?.clicks_by_device || [],
            t("Device breakdown"),
            (item) => getDeviceIcon(item?._id),
          )}
          {renderBreakdown(
            stats?.clicks_by_browser || [],
            t("Browser breakdown"),
            () => (
              <Globe className="h-4 w-4" />
            ),
          )}
        </div>
      )}

        </TabsContent>

        <TabsContent value="clicks" className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>{t("Recent clicks")}</CardTitle>
            <CardDescription>
              {t("Recent activity for this campaign")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {clicks.length === 0 ? (
              <div className="py-8 text-center">
                <MousePointer className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
                <p className="text-muted-foreground">
                  {t("No clicks for this campaign yet")}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {clicks.map((click, index) => (
                  <div
                    key={click.id || click.click_id || index}
                    className="flex items-center justify-between rounded-lg border border-border p-3"
                    data-testid={`click-item-${click.id || click.click_id || index}`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-surface-2">
                        {getDeviceIcon(click.device_type)}
                      </div>
                      <div>
                        <div className="flex items-center space-x-2 text-sm">
                          <span className="font-medium">
                            {click.device_type || t("Unknown")}
                          </span>
                          {click.is_unique && (
                            <Badge variant="secondary" className="text-xs">
                              {t("Unique")}
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {click.browser || t("Unknown")} •{" "}
                          {click.os || t("Unknown")}
                        </div>
                        {click.country && (
                          <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                            <Globe className="h-3 w-3" />
                            <span>
                              {click.city && click.city !== "Unknown"
                                ? `${click.city}, `
                                : ""}
                              {click.country}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-muted-foreground">
                        {formatDate(click.timestamp)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {click.ip_address || t("Unknown")}
                      </div>
                    </div>
                  </div>
                ))}

                {clicks.length >= 10 && (
                  <div className="pt-2 text-center">
                    <p className="text-sm text-muted-foreground">
                      {t("Showing latest 10 clicks")}
                    </p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
        <ClickIssues campaignId={id} redirectMode={(campaign && campaign.redirect_mode) || ''} />
        <IncomingPostbacks campaignId={id} />
        </TabsContent>

        <TabsContent value="settings" className="space-y-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t("Campaign info")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                {t("Name")}
              </label>
              <p className="text-foreground">{campaign.name || t("Unknown")}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground">
                {t("Description")}
              </label>
              <p className="text-foreground">
                {campaign.description || t("No description")}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground">
                {t("Target URL")}
              </label>
              <div className="flex items-center space-x-2">
                <p className="truncate text-primary">
                  {campaign.target_url || t("Unknown")}
                </p>
                <Button variant="ghost" size="sm" aria-label={t('Open target URL')} title={t('Open target URL')} onClick={openTargetUrl}>
                  <ExternalLink className="h-3 w-3" />
                </Button>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground">
                {t("Tracking ID")}
              </label>
              <div className="flex items-center space-x-2">
                <code className="rounded border border-border bg-surface-2 px-2 py-1 font-mono text-sm">
                  {campaign.tracking_id || t("Unknown")}
                </code>
                <Button variant="ghost" size="sm" aria-label={t('Copy tracking link')} title={t('Copy tracking link')} onClick={copyTrackingUrl}>
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground">
                {t("Tracking link")}
              </label>
              <div className="flex items-center space-x-2">
                <code className="flex-1 truncate rounded border border-border bg-surface-2 px-2 py-1 font-mono text-sm">
                  {trackingUrl}
                </code>
                <Button variant="ghost" size="sm" aria-label={t('Copy tracking link')} title={t('Copy tracking link')} onClick={copyTrackingUrl}>
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground">
                {t("Created")}
              </label>
              <div className="flex items-center space-x-1">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <p className="text-foreground">
                  {formatDate(campaign.created_at)}
                </p>
              </div>
            </div>

            {campaign.updated_at &&
              campaign.updated_at !== campaign.created_at && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    {t("Updated")}
                  </label>
                  <div className="flex items-center space-x-1">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <p className="text-foreground">
                      {formatDate(campaign.updated_at)}
                    </p>
                  </div>
                </div>
              )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader
            role="button"
            tabIndex={0}
            aria-expanded={showIntegration}
            className="cursor-pointer select-none"
            onClick={() => setShowIntegration((v) => !v)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setShowIntegration((v) => !v);
              }
            }}
          >
            <CardTitle className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-2">
                {showIntegration ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
                {t("Integration")}
              </span>
              <span className="text-xs font-normal text-muted-foreground">
                {showIntegration ? t("Hide") : t("Show")}
              </span>
            </CardTitle>
            <CardDescription>
              {t("Headless click registration and the JS adapter for landing pages.")}
            </CardDescription>
          </CardHeader>
          {showIntegration && (
          <CardContent className="space-y-4">
            <div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-muted-foreground">{t("Click API (server-to-server)")}</label>
                <Button variant="ghost" size="sm" aria-label={t('Copy curl command')} title={t('Copy curl command')} onClick={() => { navigator.clipboard.writeText(clickCurl); toast.success(t("Copied")); }}>
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
              <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-all rounded border border-border bg-surface-2 p-2 font-mono text-xs">{clickCurl}</pre>
              <p className="mt-1 text-xs text-muted-foreground">{t("POST /v1/click with an API key (write scope). Returns the routing decision without a browser redirect.")}</p>
            </div>
            {adapterSnippet ? (
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-muted-foreground">{t("JS adapter snippet")}</label>
                  <Button variant="ghost" size="sm" aria-label={t('Copy JS snippet')} title={t('Copy JS snippet')} onClick={() => { navigator.clipboard.writeText(adapterSnippet); toast.success(t("Copied")); }}>
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
                <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-all rounded border border-border bg-surface-2 p-2 font-mono text-xs">{adapterSnippet}</pre>
                <p className="mt-1 text-xs text-muted-foreground">{t("Paste on a landing page to auto-append the click id and forward the visitor.")}</p>
              </div>
            ) : null}
            {snippets ? (
              <>
                {[
                  ["Direct link", snippets.direct_link],
                  ["HTML link", snippets.html_link],
                  ["iFrame embed", snippets.iframe],
                  ["JS injector (async)", snippets.script_async],
                  ["Email tracking pixel", snippets.email_pixel],
                  ["PHP redirect (server-side)", snippets.php_redirect],
                  ["WordPress shortcode", snippets.wordpress],
                ]
                  .filter(([, value]) => value)
                  .map(([label, value]) => (
                    <div key={label}>
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-muted-foreground">{t(label)}</label>
                        <Button variant="ghost" size="sm" aria-label={t('Copy snippet')} title={t('Copy snippet')} onClick={() => { navigator.clipboard.writeText(value); toast.success(t("Copied")); }}>
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                      <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-all rounded border border-border bg-surface-2 p-2 font-mono text-xs">{value}</pre>
                    </div>
                  ))}
                {snippets.qr_svg ? (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">{t("QR code")}</label>
                    <div
                      className="mt-1 inline-block rounded border border-border bg-white p-2 [&_svg]:h-32 [&_svg]:w-32"
                      dangerouslySetInnerHTML={{ __html: snippets.qr_svg }}
                    />
                    <p className="mt-1 text-xs text-muted-foreground">{t("Scan to open the tracking link on a device.")}</p>
                  </div>
                ) : null}
              </>
            ) : null}
          </CardContent>
          )}
        </Card>

          </div>
      <CampaignParamsAndPostbacks campaign={campaign} onUpdated={setCampaign} />

      <PoolLinkGenerator campaign={campaign} domains={domains} />

      {/* Linked CAPI forwarders */}
      {linkedForwarders.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Send className="w-4 h-4" /> {t('Linked forwarders')}
            </CardTitle>
            <CardDescription>{t('Conversion forwarders that fire for this campaign')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {linkedForwarders.map(fw => (
                <div key={fw.id} className="flex items-center justify-between text-sm border border-border rounded-md px-3 py-2">
                  <div className="flex items-center gap-2">
                    <Send className="w-3 h-3 text-muted-foreground" />
                    <span>{fw.name}</span>
                    <Badge variant="outline" className="text-xs">{fw.provider === 'google_ads' ? 'Google Ads' : fw.provider === 'fb_capi' ? 'FB CAPI' : fw.provider}</Badge>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{fw.event_name}</span>
                    {fw.is_active
                      ? <Badge className="bg-primary/15 text-primary border border-primary/30 text-xs">{t('Active')}</Badge>
                      : <Badge variant="outline" className="text-xs">{t('Inactive')}</Badge>}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <PublicDashboardLinks campaignId={id} />

        <CampaignMaintenance campaignId={id} />

        </TabsContent>
      </Tabs>
    </div>
  );
}
